import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FEEDBACK_STATUSES } from "@/data/forge";

const TABS = ["overview", "moderation", "feedback", "users", "settings"];
const field =
  "w-full bg-white/[0.04] border border-white/12 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-ember";

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [posts, setPosts] = useState([]);
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/", { replace: true });
  }, [loading, user, navigate]);

  const loadAll = useCallback(async () => {
    try {
      const [s, p, u, cfg] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/posts"),
        api.get("/admin/users"),
        api.get("/settings"),
      ]);
      setStats(s.data);
      setPosts(p.data);
      setUsers(u.data);
      setSettings(cfg.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => {
    if (user?.role === "admin") loadAll();
  }, [user, loadAll]);

  const patchPost = async (postId, body) => {
    try {
      const { data } = await api.patch(`/admin/posts/${postId}`, body);
      setPosts((list) => list.map((p) => (p.post_id === postId ? data : p)));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deletePost = async (postId) => {
    await api.delete(`/posts/${postId}`);
    setPosts((list) => list.filter((p) => p.post_id !== postId));
    toast.success("Deleted");
  };

  const toggleBan = async (userId) => {
    try {
      const { data } = await api.post(`/admin/users/${userId}/ban`);
      setUsers((list) => list.map((u) => (u.user_id === userId ? data : u)));
      toast.success(data.banned ? "User banned from posting" : "Ban lifted");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", settings);
      setSettings(data);
      toast.success("Site updated — live now.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!user || user.role !== "admin") return <main className="min-h-screen pt-40" />;

  const feedback = posts.filter((p) => p.kind === "feedback").sort((a, b) => b.like_count - a.like_count);

  return (
    <main data-testid="admin-page" className="min-h-screen pt-28 pb-24">
      <div className="mx-auto w-[min(1100px,calc(100%-32px))]">
        <div className="eyebrow-label mb-3">Owner only</div>
        <h1 className="headline text-4xl sm:text-5xl mb-10">
          <span className="text-white">The </span>
          <span className="text-forge">Anvil</span>
        </h1>

        <div className="flex gap-2 mb-10 flex-wrap">
          {TABS.map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t}`}
              onClick={() => setTab(t)}
              className={`display text-[11px] tracking-[0.2em] px-5 py-3 rounded-full border capitalize transition-colors ${
                tab === t ? "border-forge text-forge bg-forge/10" : "border-white/10 text-white/45 hover:text-white"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "overview" && stats && (
          <div data-testid="admin-overview">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              {Object.entries(stats.counts).map(([key, value]) => (
                <div key={key} className="panel rounded-2xl p-6">
                  <div className="headline text-4xl text-forge">{value}</div>
                  <div className="display text-[10px] tracking-[0.25em] text-white/40 mt-2 capitalize">{key}</div>
                </div>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="panel rounded-2xl p-6">
                <h3 className="headline text-xl mb-4">Forge of the week</h3>
                <p className="text-xs text-white/40 mb-4">
                  The most-liked creation of the last seven days is spotlit on the landing page
                  automatically. You can email the author to let them know.
                </p>
                <button
                  data-testid="notify-winner-btn"
                  onClick={async () => {
                    try {
                      const { data } = await api.post("/admin/notify-winner");
                      toast.success(`Emailed ${data.sent_to} about "${data.title}"`);
                    } catch (e) {
                      toast.error(apiError(e));
                    }
                  }}
                  className="display text-[11px] tracking-[0.2em] px-5 py-3 rounded-full border border-forge text-forge hover:bg-forge hover:text-white transition-colors"
                >
                  Email this week's winner
                </button>
              </div>
              <div className="panel rounded-2xl p-6">
                <h3 className="headline text-xl mb-4">Newest signups</h3>
                {stats.recent_users.map((u) => (
                  <div key={u.user_id} className="flex justify-between text-xs text-white/50 py-2 border-b border-white/5">
                    <span>{u.name}</span>
                    <span>{timeAgo(u.created_at)}</span>
                  </div>
                ))}
              </div>
              <div className="panel rounded-2xl p-6">
                <h3 className="headline text-xl mb-4">Newest posts</h3>
                {stats.recent_posts.map((p) => (
                  <Link
                    key={p.post_id}
                    to={`/community/${p.post_id}`}
                    className="flex justify-between text-xs text-white/50 py-2 border-b border-white/5 hover:text-ember"
                  >
                    <span className="truncate pr-3">{p.title}</span>
                    <span>{timeAgo(p.created_at)}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "moderation" && (
          <div className="space-y-3" data-testid="admin-moderation">
            {posts.map((p) => (
              <div key={p.post_id} className="panel rounded-2xl p-5 flex flex-wrap items-center gap-4 justify-between">
                <div className="min-w-[220px]">
                  <Link to={`/community/${p.post_id}`} className="headline text-lg hover:text-ember">
                    {p.title}
                  </Link>
                  <div className="text-[11px] text-white/35 mt-1">
                    {p.kind} · {p.author_name} · {timeAgo(p.created_at)} {p.hidden && "· HIDDEN"}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    data-testid={`hide-post-${p.post_id}`}
                    onClick={() => patchPost(p.post_id, { hidden: !p.hidden })}
                    className="text-[11px] px-4 py-2 rounded-full border border-white/15 text-white/60 hover:text-ember hover:border-ember/40 transition-colors"
                  >
                    {p.hidden ? "Unhide" : "Hide"}
                  </button>
                  <button
                    data-testid={`delete-post-admin-${p.post_id}`}
                    onClick={() => deletePost(p.post_id)}
                    className="text-[11px] px-4 py-2 rounded-full border border-white/15 text-white/40 hover:text-forge hover:border-forge/40 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "feedback" && (
          <div className="space-y-3" data-testid="admin-feedback">
            {feedback.length === 0 && <p className="text-sm text-white/30">No feedback yet.</p>}
            {feedback.map((p) => (
              <div key={p.post_id} className="panel rounded-2xl p-5 flex flex-wrap items-center gap-4 justify-between">
                <div className="min-w-[240px]">
                  <Link to={`/community/${p.post_id}`} className="headline text-lg hover:text-ember">
                    {p.title}
                  </Link>
                  <div className="text-[11px] text-white/35 mt-1">
                    {p.feedback_type} · {p.like_count} votes · {p.author_name}
                  </div>
                </div>
                <select
                  data-testid={`feedback-status-${p.post_id}`}
                  value={p.status || "open"}
                  onChange={(e) => patchPost(p.post_id, { status: e.target.value })}
                  className="text-xs bg-white/[0.04] border border-white/12 rounded-full px-4 py-2 text-white/70 focus:outline-none focus:border-ember"
                >
                  {FEEDBACK_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}

        {tab === "users" && (
          <div className="space-y-2" data-testid="admin-users">
            {users.map((u) => (
              <div key={u.user_id} className="panel rounded-2xl p-5 flex items-center justify-between gap-4">
                <div>
                  <Link to={`/u/${u.user_id}`} className="text-sm text-white hover:text-ember">
                    {u.name}
                  </Link>
                  <div className="text-[11px] text-white/35">
                    {u.email} · {u.role} {u.banned && "· BANNED"}
                  </div>
                </div>
                {u.role !== "admin" && (
                  <button
                    data-testid={`ban-user-${u.user_id}`}
                    onClick={() => toggleBan(u.user_id)}
                    className="text-[11px] px-4 py-2 rounded-full border border-white/15 text-white/50 hover:text-forge hover:border-forge/40 transition-colors"
                  >
                    {u.banned ? "Unban" : "Ban"}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "settings" && settings && (
          <div className="space-y-6 max-w-[720px]" data-testid="admin-settings">
            <div className="panel rounded-2xl p-6 space-y-4">
              <h3 className="headline text-xl">Hero</h3>
              <input
                data-testid="settings-headline"
                className={field}
                value={settings.hero_headline}
                onChange={(e) => setSettings({ ...settings, hero_headline: e.target.value })}
                placeholder="Headline"
              />
              <input
                data-testid="settings-tagline"
                className={field}
                value={settings.hero_tagline}
                onChange={(e) => setSettings({ ...settings, hero_tagline: e.target.value })}
                placeholder="Tagline"
              />
              <textarea
                data-testid="settings-sub"
                className={`${field} min-h-[90px]`}
                value={settings.hero_sub}
                onChange={(e) => setSettings({ ...settings, hero_sub: e.target.value })}
                placeholder="Sub-line"
              />
              <input
                data-testid="settings-question"
                className={field}
                value={settings.hero_question}
                onChange={(e) => setSettings({ ...settings, hero_question: e.target.value })}
                placeholder="Hero question"
              />
            </div>

            <div className="panel rounded-2xl p-6 space-y-4">
              <h3 className="headline text-xl">Store links</h3>
              <input
                data-testid="settings-app-store"
                className={field}
                value={settings.app_store_url}
                onChange={(e) => setSettings({ ...settings, app_store_url: e.target.value })}
              />
              <input
                data-testid="settings-play-store"
                className={field}
                value={settings.play_store_url}
                onChange={(e) => setSettings({ ...settings, play_store_url: e.target.value })}
              />
            </div>

            <div className="panel rounded-2xl p-6 space-y-5">
              <h3 className="headline text-xl">Testimonials</h3>
              {settings.testimonials.map((t, i) => (
                <div key={i} className="space-y-2 border-b border-white/5 pb-4">
                  <textarea
                    data-testid={`settings-testimonial-quote-${i}`}
                    className={`${field} min-h-[70px]`}
                    value={t.quote}
                    onChange={(e) => {
                      const next = [...settings.testimonials];
                      next[i] = { ...t, quote: e.target.value };
                      setSettings({ ...settings, testimonials: next });
                    }}
                  />
                  <div className="grid sm:grid-cols-2 gap-2">
                    <input
                      className={field}
                      value={t.name}
                      placeholder="Name"
                      onChange={(e) => {
                        const next = [...settings.testimonials];
                        next[i] = { ...t, name: e.target.value };
                        setSettings({ ...settings, testimonials: next });
                      }}
                    />
                    <input
                      className={field}
                      value={t.meta || ""}
                      placeholder="Meta"
                      onChange={(e) => {
                        const next = [...settings.testimonials];
                        next[i] = { ...t, meta: e.target.value };
                        setSettings({ ...settings, testimonials: next });
                      }}
                    />
                  </div>
                  <button
                    onClick={() =>
                      setSettings({
                        ...settings,
                        testimonials: settings.testimonials.filter((_, x) => x !== i),
                      })
                    }
                    className="text-[11px] text-white/30 hover:text-forge"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                data-testid="add-testimonial"
                onClick={() =>
                  setSettings({
                    ...settings,
                    testimonials: [...settings.testimonials, { quote: "", name: "", meta: "" }],
                  })
                }
                className="text-[11px] text-ember hover:text-forge"
              >
                + Add testimonial
              </button>
            </div>

            <div className="panel rounded-2xl p-6 space-y-5">
              <h3 className="headline text-xl">FAQ</h3>
              {settings.faqs.map((f, i) => (
                <div key={i} className="space-y-2 border-b border-white/5 pb-4">
                  <input
                    data-testid={`settings-faq-q-${i}`}
                    className={field}
                    value={f.q}
                    placeholder="Question"
                    onChange={(e) => {
                      const next = [...settings.faqs];
                      next[i] = { ...f, q: e.target.value };
                      setSettings({ ...settings, faqs: next });
                    }}
                  />
                  <textarea
                    className={`${field} min-h-[80px]`}
                    value={f.a}
                    placeholder="Answer"
                    onChange={(e) => {
                      const next = [...settings.faqs];
                      next[i] = { ...f, a: e.target.value };
                      setSettings({ ...settings, faqs: next });
                    }}
                  />
                  <button
                    onClick={() => setSettings({ ...settings, faqs: settings.faqs.filter((_, x) => x !== i) })}
                    className="text-[11px] text-white/30 hover:text-forge"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                data-testid="add-faq"
                onClick={() => setSettings({ ...settings, faqs: [...settings.faqs, { q: "", a: "" }] })}
                className="text-[11px] text-ember hover:text-forge"
              >
                + Add FAQ
              </button>
            </div>

            <div className="panel rounded-2xl p-6 flex items-center justify-between">
              <div>
                <h3 className="headline text-xl">Community</h3>
                <p className="text-xs text-white/40 mt-1">
                  {settings.community_open ? "Open — anyone signed in can post." : "Read-only — posting is off."}
                </p>
              </div>
              <button
                data-testid="toggle-community"
                onClick={() => setSettings({ ...settings, community_open: !settings.community_open })}
                className={`display text-[11px] tracking-[0.2em] px-5 py-3 rounded-full border transition-colors ${
                  settings.community_open ? "border-[#35c978] text-[#35c978]" : "border-white/20 text-white/50"
                }`}
              >
                {settings.community_open ? "Open" : "Read-only"}
              </button>
            </div>

            <button
              data-testid="save-settings-btn"
              onClick={saveSettings}
              disabled={saving}
              className="display text-sm tracking-[0.2em] px-8 py-4 rounded-full bg-forge text-white hover:bg-ember disabled:opacity-50 transition-colors"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        )}
      </div>
    </main>
  );
}
